import { useMemo } from "react";

import { cn } from "~/lib/utils";
import { useI18n } from "~/i18n";
import { MorrowPersonAvatar } from "../workspace/MorrowPersonCard";
import type { ActivityEvent } from "./activitiesTypes";
import { ActivityLifecycleStepper } from "./ActivityLifecycleStepper";
import {
  activityKindIcon,
  activityKindLabelKey,
  activityLifecycleSteps,
  activityStatusLabelKey,
  activityTimelineDotClass,
  formatActivityTime,
  toolkitLogoUrl,
} from "./activityPresentation";

function compareByOccurredAt(a: ActivityEvent, b: ActivityEvent): number {
  return new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime();
}

export function ActivityTimeline({
  events,
  selectedId,
  onSelect,
}: {
  readonly events: readonly ActivityEvent[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}) {
  const { t, locale } = useI18n();

  const sorted = useMemo(() => [...events].sort(compareByOccurredAt), [events]);

  if (sorted.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border/70 px-3 py-8 text-center text-xs text-muted-foreground">
        {t("activities.timeline.empty")}
      </p>
    );
  }

  return (
    <ol className="flex flex-col">
      {sorted.map((event, index) => {
        const selected = selectedId === event.id;
        const logo = toolkitLogoUrl(event.refs.toolkit);
        const KindIcon = activityKindIcon(event.kind);
        const steps = activityLifecycleSteps(event);
        const isLast = index === sorted.length - 1;

        return (
          <li key={event.id} className="flex gap-3">
            <div className="flex w-11 shrink-0 flex-col items-end pt-3">
              <time className="text-[10px] tabular-nums text-muted-foreground">
                {formatActivityTime(event.occurredAt, locale)}
              </time>
              <div className="mt-2 flex flex-1 flex-col items-center">
                <span
                  className={cn(
                    "size-2 shrink-0 rounded-full",
                    activityTimelineDotClass(event.status),
                  )}
                  aria-hidden
                />
                {!isLast ? (
                  <span className="mt-1 w-px flex-1 min-h-3 bg-border/80" aria-hidden />
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onSelect(event.id)}
              className={cn(
                "mb-2 flex min-w-0 flex-1 flex-col gap-2 rounded-xl border border-border/70 bg-card px-3 py-2.5 text-left transition-colors hover:bg-muted/35",
                selected && "border-primary/40 bg-muted/25 ring-1 ring-primary/25",
              )}
            >
              <div className="flex min-w-0 items-start gap-2.5">
                <MorrowPersonAvatar
                  name={event.actor.name}
                  avatarSeed={event.actor.avatarSeed}
                  size="sm"
                  className="mt-0.5 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{event.title}</p>
                  <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                    {event.summary}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
                    <span className="font-medium text-foreground/80">{event.actor.name}</span>
                    <span aria-hidden>·</span>
                    <span>{t(activityStatusLabelKey(event.status))}</span>
                    <span aria-hidden>·</span>
                    <span className="inline-flex items-center gap-0.5">
                      <KindIcon className="size-2.5 shrink-0" aria-hidden />
                      {t(activityKindLabelKey(event.kind))}
                    </span>
                    {logo ? (
                      <img
                        src={logo}
                        alt=""
                        className="size-3 rounded object-contain"
                        loading="lazy"
                      />
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="max-w-[11rem] ps-9 sm:ps-10">
                <ActivityLifecycleStepper steps={steps} compact />
              </div>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
